import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

const ROUTE_PATTERN_MAP: Record<string, string> = {
  "/": "/",
  "/home": "/",
  "/listing/:id": "/listing/{id}",
  "/articles/:slug": "/articles/{slug}",
  "/profile/:id": "/profile/{id}",
  "/search": "/search",
  "/contact": "/contact",
  "/how-it-works": "/how-it-works",
  "/terms": "/terms",
  "/privacy": "/privacy",
  "/unsubscribe": "/unsubscribe",
  "/login": "/login",
  "/register": "/register",
  "/dashboard": "/dashboard",
  "/publish": "/publish",
  "/admin": "/admin",
};

function toPattern(pathname: string): string {
  if (pathname.startsWith("/perfil/")) return "/perfil/:state/:city/:slug";
  if (ROUTE_PATTERN_MAP[pathname]) return ROUTE_PATTERN_MAP[pathname];
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "/";
  const pattern = "/" + segments.map((seg, idx) => (idx === 0 && seg === "listing" ? "{id}" : seg === "articles" ? "{slug}" : seg === "profile" ? "{id}" : seg)).join("/");
  return pattern;
}

export default function SEOHead() {
  const location = useLocation();
  const pattern = useMemo(() => toPattern(location.pathname), [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const skipRouteApi = pattern === "/admin" || pattern === "/login" || pattern === "/registrar" || pattern === "/esqueci-senha" || pattern === "/unsubscribe" || pattern === "/dashboard" || pattern === "/publish" || pattern === "/publicar" || pattern === "/fale-conosco" || pattern === "/como-funciona" || pattern === "/termos" || pattern === "/privacidade" || pattern.startsWith("/conta/") || pattern.startsWith("/admin/");
    const skipJsonLdApi = pattern === "/admin" || pattern === "/login" || pattern === "/registrar" || pattern === "/esqueci-senha" || pattern === "/unsubscribe" || pattern === "/dashboard" || pattern === "/publish" || pattern === "/publicar" || pattern === "/fale-conosco" || pattern === "/como-funciona" || pattern === "/termos" || pattern === "/privacidade";
    if (!skipRouteApi) {
      api.get("/seo/route", { params: { pattern } }).then(({ data }) => {
        if (cancelled || !data) return;
        const config = data as Record<string, unknown>;
        const title = typeof config.title === "string" ? config.title : typeof config.title_template === "string" ? String(config.title_template) : "";
        const description = typeof config.description === "string" ? config.description : typeof config.description_template === "string" ? String(config.description_template) : "";
        const ogImage = typeof config.og_image === "string" ? config.og_image : "";
        const ogType = typeof config.og_type === "string" ? config.og_type : "website";
        const twitterCard = typeof config.twitter_card === "string" ? config.twitter_card : "summary_large_image";
        const robots = typeof config.robots === "string" ? config.robots : "index,follow";

        if (title) document.title = title;
        const setMeta = (name: string, content: string) => {
          let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
          if (!tag) {
            tag = document.createElement("meta");
            tag.name = name;
            document.head.appendChild(tag);
          }
          tag.content = content;
        };
        const setProp = (prop: string, content: string) => {
          let tag = document.querySelector(`meta[property="${prop}"]`) as HTMLMetaElement | null;
          if (!tag) {
            tag = document.createElement("meta");
            tag.setAttribute("property", prop);
            document.head.appendChild(tag);
          }
          tag.content = content;
        };
        if (description) {
          setMeta("description", description);
          setProp("og:description", description);
          setProp("twitter:description", description);
        }
        if (ogImage) {
          setProp("og:image", ogImage);
          setProp("twitter:image", ogImage);
        }
        setProp("og:type", ogType);
        setProp("og:url", window.location.href);
        setProp("twitter:card", twitterCard);
        setMeta("robots", robots);
      }).catch(() => {});
    }

    if (!skipJsonLdApi) {
      api.get("/seo/jsonld", { params: { pattern } }).then(({ data }) => {
        if (cancelled || !data) return;
        const graph = data as Record<string, unknown>;
        let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement | null;
        if (!script) {
          script = document.createElement("script");
          script.type = "application/ld+json";
          document.head.appendChild(script);
        }
        script.text = JSON.stringify(graph);
      }).catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [pattern]);

  return null;
}
